import React, { useEffect } from 'react';
import PageHeader from '../innovher/PageHeader';
import { IMG } from '../../lib/images';
import { Link } from 'react-router-dom';
import SEO from '../SEO';

export default function RssPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const articles = [
    {
      title: "Rajasthan Startup Stories Episode 1: Aesthetics & Algorithms: Building Lifestyle Brands That Last",
      summary: "The Rajasthan startup ecosystem is evolving rapidly, with founders, investors, and mentors shaping the future of innovation in the state. To capture these journeys, Rajasthan Startup Stories (RSS) has been launched by Innovher, a startup acceleration platform dedicated to building and scaling high-potential ventures.",
      date: "Sep 8, 2025",
      videoSrc: "https://www.youtube.com/embed/2eb8DaS-QiQ?si=jbd6tkcFMo255N69",
      image: IMG.blog1,
      category: "Startup & Business"
    },
    {
      title: "Rajasthan Startup Stories Episode 2: Experience as a Brand: Reimagining Hospitality in India",
      summary: "Hospitality in India is evolving. It’s no longer just about providing food and stay, it’s about curating memorable, brand-driven experiences. From boutique homestays and immersive dining to community-driven social spaces, the industry is redefining itself as experience-first.",
      date: "Sep 15, 2025",
      videoSrc: "https://www.youtube.com/embed/tv6WiyU89VI?si=ubnN_nkEGgvab2qF",
      image: IMG.blog2,
      category: "Startup & Business"
    },
    {
      title: "Rajasthan Startup Stories Episode 3: How Rajasthan is Shaping India’s Tech Future",
      summary: "Over the last decade, Rajasthan has quietly shifted from a traditional business state into one of India’s most promising innovation corridors. Jaipur, Jodhpur, Udaipur, Kota, and emerging districts now host founders building SaaS, AI, D2C, manufacturing, and deep-tech ventures.",
      date: "Sep 17, 2025",
      videoSrc: "https://www.youtube.com/embed/HBtkzRDfBxc?si=RCyAsC3LNFlZeYW4",
      image: IMG.blog3,
      category: "Startup & Business"
    },
    {
      title: "From Local to Loved: Cracking the D2C Growth Code in Tier-2 India | Rajasthan Startup Stories",
      summary: "Discover how D2C brands from Tier-2 India are scaling successfully. Insights from angel investor Sourabh Sharma, Nuska Kitchen founder Viral Tiwari, and host Dr. Shweta Choudhary on funding, branding, unit economics, and growth strategy.",
      date: "Feb 13, 2026",
      videoSrc: "https://www.youtube.com/embed/EEfwS-GgJcs?si=rTbvRtLUCp6HCHQv",
      image: IMG.collab1,
      category: "Startup & Business"
    }
  ];

  return (
    <>
      <SEO 
        title="Rajasthan Startup Stories"
        description="Rajasthan Startup Stories, an InnovHer podcast documenting founders, businesses and ecosystem builders shaping entrepreneurship across Rajasthan."
        keywords="Rajasthan Startup Stories, InnovHer podcast, iSSi, Jaipur founders, startup interviews Rajasthan"
      />
      <PageHeader
        eyebrow="Publications"
        title={<>Rajasthan <span className="text-[#ff8ec5]">Startup Stories</span></>}
        subtitle="Capturing the journeys of founders, investors, and mentors shaping the future of innovation in Rajasthan."
        image={IMG.collab1}
        breadcrumbs={[{ label: 'Publication', to: '#' }, { label: 'RSS' }]}
      />

      <section className="relative bg-[#08080a] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="reveal-up max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">Latest Publications</h2>
            <p className="text-lg text-white/60">Explore our latest stories, insights, and interviews from the thriving startup ecosystem.</p>
          </div>

          <div className="grid grid-cols-1 gap-12 max-w-4xl mx-auto">
            {articles.map((article, i) => (
              <article key={i} className="reveal-up group relative flex flex-col md:flex-row items-center gap-8 bg-white/5 border border-white/10 rounded-3xl p-6 transition-all duration-300 hover:bg-white/10">
                <div className="w-full md:w-2/5 aspect-[4/3] rounded-2xl overflow-hidden shrink-0">
                  {article.videoSrc ? (
                    <iframe 
                      className="w-full h-full"
                      src={article.videoSrc} 
                      title={article.title} 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen 
                    />
                  ) : (
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-[#ff8ec5]/20 text-[#ff8ec5] rounded-full">
                      {article.category}
                    </span>
                    <span className="text-sm text-white/40">{article.date}</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#ff8ec5] transition-colors">
                    {article.title}
                  </h3>
                  
                  <p className="text-white/60 leading-relaxed mb-6 line-clamp-3">
                    {article.summary}
                  </p>
                  
                  <div className="mt-auto">
                    <Link to="#" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#ff8ec5] transition-colors">
                      Read Full Story
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
          
        </div>
      </section>
    </>
  );
}
